import React, { useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Grid from 'components/Grid';
import { useDispatch, useSelector } from 'react-redux';
import { chatActions, chatSelectors, chatTypes } from 'redux/chat';
import Avatar from 'components/Avatar';
import Button from 'components/Button';
import FormField from 'components/FinalForm/Fields/FormField';
import Form from 'components/FinalForm/Form';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';

import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import useFileInput from 'hooks/useFileInput';
import InputAdornment from '@mui/material/InputAdornment';
import DialogActions from '@mui/material/DialogActions';
import TextLoader from 'components/TextLoader';
import DialogTitle from '@mui/material/DialogTitle';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

const ManageConversationDialog = ({ conversationId, onClose, ...rest }) => {

  const dispatch = useDispatch();
  const { t } = useTranslation(['chat']);

  const conversation = useSelector(chatSelectors.getConversationSelector)(conversationId);

  const onChange = file => {
    if (!file) {
      return;
    }

    dispatch(chatActions.updateConversation({ conversationId, photo: file }));
  };

  const { open: onUploadPhotoClick } = useFileInput({
    onChange,
    accept: 'image/png,image/jpeg,.jpg',
  });

  const onDeletePhotoClick = () => {
    dispatch(chatActions.updateConversation({ conversationId, photo: 0 }));
  };

  const initialValues = useMemo(() => ({
    conversationId: conversation?.id,
    subject: conversation?.storedSubject,
  }), [conversation?.id, conversation?.storedSubject]);

  return (
    <Dialog maxWidth="xs" fullWidth onClose={onClose} {...rest}>
      <DialogTitle>
        Manage conversation
      </DialogTitle>
      <DialogContent align="center">
        {!conversation && <TextLoader />}

        <Form
          start={chatTypes.updateConversation}
          resolve={chatTypes.updateConversationSuccess}
          reject={chatTypes.updateConversationFailure}

          hidden={!conversation}

          initialValues={initialValues}
        >
          {({ handleSubmit, dirty }) => (
            <Grid container direction="column" wrap="nowrap" spacing={4}>
              <Grid item>
                <Grid container direction="column" wrap="nowrap" spacing={2}>
                  <Grid item>
                    <Avatar
                      size={100}
                      src={conversation?.photoUrl}
                      name={conversation?.subject}
                    />
                  </Grid>
                  <Grid item>
                    <Grid container spacing={2} justifyContent="center">
                      <Grid item>
                        <Button size="small" color="default" startIcon={<CameraAltOutlinedIcon />} onClick={onUploadPhotoClick}>
                          {t('manageConversation.photo.uploadButton')}
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button size="small" color="default" startIcon={<DeleteOutlineRoundedIcon />} disabled={!conversation?.storedPhotoUrl} onClick={onDeletePhotoClick}>
                          {t('manageConversation.photo.removeButton')}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <FormField
                  name="subject"
                  margin="none"
                  placeholder={conversation?.subject}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment edge="end">
                        <Button variant="text" disabled={!dirty} onClick={handleSubmit} size="small">{t('manageConversation.subject.saveButton')}</Button>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          )}
        </Form>
      </DialogContent>
      <DialogActions>
        <Grid container direction="row" wrap="nowrap" alignItems="center" justifyContent="flex-end">
          <Grid item>
            <Button variant="text" onClick={onClose}>{t('manageConversation.closeButton')}</Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
};

ManageConversationDialog.propTypes = {
  conversationId: PropTypes.any.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ManageConversationDialog;
